// Story 8.10 — "Your details": the legal identity a settlement voucher names.
//
// ---------------------------------------------------------------------------
// WHY THIS SECTION EXISTS AT ALL
// ---------------------------------------------------------------------------
//
// A self-billed settlement is a bookkeeping voucher (afregningsbilag), and one
// that names no counterparty is not a voucher — bogføringsloven § 9 requires the
// record to show who the transaction was with. So the server refuses to raise a
// settlement for a creator with no legal identity on file, and tells them to add
// it HERE. That refusal used to point at nothing: an approved creator cannot
// edit their application (apply and draft-save both refuse an approved
// applicant) and the admin review surface only touches pending rows. This
// section plus `PUT /creators/me/identity` is the remedy.
//
// ---------------------------------------------------------------------------
// SHOWN ALWAYS, NOT ONLY WHEN MISSING
// ---------------------------------------------------------------------------
//
// Greenhold holds this creator's legal name and home address and prints them on
// a document that goes into its own books. A surface that only appears when
// something is wrong would mean the creator can never simply CHECK what is held
// about them, or correct a typo in the address a voucher is about to carry.
//
// ---------------------------------------------------------------------------
// THE FIELDSET IS DUPLICATED FROM `CreatorApplicationForm`, DELIBERATELY
// ---------------------------------------------------------------------------
//
// Extraction was considered and rejected: that fieldset reads eight `useState`
// pairs, a `fieldErrors` map and a `clearFieldError` callback from its parent,
// sits next to `IdentityVerifyField` and the draft-persistence round-trip, and
// hardcodes DOM ids (`legal_name`, `business_status_private`, …) that would
// COLLIDE if both forms were ever mounted on one page. Pulling it out means
// restructuring the application form's state, which is another story's file.
//
// So the markup is duplicated and the ids are prefixed `identity_`. What is NOT
// duplicated is the VALIDATION: the server runs the same `validate_identity` for
// this endpoint as for apply, so the rules live in one place and this form does
// only the cheap local checks that save a round-trip.
//
// ⛔ THE NUMBER IS LABELLED "Registration number", never "VAT number". A CVR
// identifies a company and says nothing about VAT registration, and this is the
// same label the voucher prints — the two must agree.

import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Button, Card, CardContent, Input, Label } from "@greenhold/components";

import type { StoredCreatorIdentity } from "./usePayoutOptions";
import { useUpdateCreatorIdentity } from "./useUpdateCreatorIdentity";

const LEGAL_NAME_MAX = 120;
const ADDRESS_LINE_MAX = 120;
const POSTAL_CODE_MAX = 20;
const CITY_MAX = 80;
const REGISTRATION_NUMBER_MAX = 32;

/** Empty until chosen — never defaulted to `private`. Which capacity a creator
 *  acts in is a declaration about themselves, and a prefilled radio makes the
 *  form declare it for them. */
type BusinessStatus = "" | "private" | "business";

function normalizeCountry(raw: string): string {
  return raw.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 2);
}

export function CreatorIdentitySection({
  identity,
  onFile,
}: {
  /** `null` means the caller is not an approved creator; this section is not
   *  rendered for them at all, which the panel decides. */
  identity: StoredCreatorIdentity;
  onFile: boolean;
}) {
  const update = useUpdateCreatorIdentity();
  const [editing, setEditing] = useState(false);

  const [legalName, setLegalName] = useState(identity.legal_name ?? "");
  const [addressLine1, setAddressLine1] = useState(identity.address_line1 ?? "");
  const [addressLine2, setAddressLine2] = useState(identity.address_line2 ?? "");
  const [postalCode, setPostalCode] = useState(identity.postal_code ?? "");
  const [city, setCity] = useState(identity.city ?? "");
  const [countryCode, setCountryCode] = useState(identity.country_code ?? "");
  const [businessStatus, setBusinessStatus] = useState<BusinessStatus>(
    identity.business_status ?? "",
  );
  const [registrationNumber, setRegistrationNumber] = useState(
    identity.business_registration_number ?? "",
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Re-seed when the server's copy changes under us (a successful save
  // invalidates the payout options query, so this component is re-rendered with
  // the stored values rather than the ones typed).
  useEffect(() => {
    if (editing) return;
    setLegalName(identity.legal_name ?? "");
    setAddressLine1(identity.address_line1 ?? "");
    setAddressLine2(identity.address_line2 ?? "");
    setPostalCode(identity.postal_code ?? "");
    setCity(identity.city ?? "");
    setCountryCode(identity.country_code ?? "");
    setBusinessStatus(identity.business_status ?? "");
    setRegistrationNumber(identity.business_registration_number ?? "");
  }, [identity, editing]);

  function clearFieldError(field: string) {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function submit() {
    // Local checks only — the authority is the server's `validate_identity`.
    const errs: Record<string, string> = {};
    if (!legalName.trim()) errs.legal_name = "Legal name is required.";
    if (!addressLine1.trim()) errs.address_line1 = "Address is required.";
    if (!postalCode.trim()) errs.postal_code = "Postal code is required.";
    if (!city.trim()) errs.city = "City is required.";
    if (countryCode.trim().length !== 2) {
      errs.country_code = "Enter a 2-letter country code, e.g. DK.";
    }
    if (!businessStatus) {
      errs.business_status =
        "Choose whether you are a private individual or a business.";
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    update.mutate(
      {
        legal_name: legalName.trim(),
        address_line1: addressLine1.trim(),
        address_line2: addressLine2.trim() || null,
        postal_code: postalCode.trim(),
        city: city.trim(),
        country_code: countryCode.trim().toUpperCase(),
        business_status: businessStatus as "private" | "business",
        business_registration_number:
          businessStatus === "business" && registrationNumber.trim()
            ? registrationNumber.trim()
            : null,
        // Per SUBMIT, not per mount.
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success("Your details are saved.");
        },
        onError: (error) => {
          if (error.field) {
            setFieldErrors({ [error.field]: error.message });
          } else {
            toast.error(error.message);
          }
        },
      },
    );
  }

  const addressLines = [
    identity.address_line1,
    identity.address_line2,
    [identity.postal_code, identity.city].filter(Boolean).join(" ") || null,
    identity.country_code,
  ].filter((l): l is string => !!l && l.length > 0);

  return (
    <div>
      <h3 className="text-lg font-heading font-semibold mb-2">Your details</h3>
      <Card>
        <CardContent className="p-6 space-y-4">
          <p className="text-sm opacity-75">
            These go on the settlement document we raise for you. A payment
            record has to name who it is with, so we need them before we can
            settle.
          </p>

          {!editing ? (
            <>
              {onFile ? (
                <dl className="text-sm space-y-1">
                  <div>
                    <dt className="text-xs uppercase tracking-wider font-mono opacity-60">
                      Legal name
                    </dt>
                    <dd>{identity.legal_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider font-mono opacity-60">
                      Address
                    </dt>
                    {addressLines.map((line) => (
                      <dd key={line}>{line}</dd>
                    ))}
                  </div>
                  {identity.business_registration_number ? (
                    <div>
                      <dt className="text-xs uppercase tracking-wider font-mono opacity-60">
                        Registration number
                      </dt>
                      <dd>{identity.business_registration_number}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : (
                <p className="text-sm">
                  We don't have your legal name and address yet. Add them and we
                  can settle your revenue share.
                </p>
              )}
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {onFile ? "Edit your details" : "Add your details"}
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="identity_legal_name">Legal name *</Label>
                <Input
                  id="identity_legal_name"
                  value={legalName}
                  autoComplete="name"
                  maxLength={LEGAL_NAME_MAX}
                  onChange={(e) => {
                    setLegalName(e.target.value);
                    clearFieldError("legal_name");
                  }}
                  aria-invalid={!!fieldErrors.legal_name}
                />
                {fieldErrors.legal_name ? (
                  <p className="text-sm text-destructive">
                    {fieldErrors.legal_name}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1">
                <Label htmlFor="identity_address_line1">Address *</Label>
                <Input
                  id="identity_address_line1"
                  value={addressLine1}
                  autoComplete="address-line1"
                  maxLength={ADDRESS_LINE_MAX}
                  onChange={(e) => {
                    setAddressLine1(e.target.value);
                    clearFieldError("address_line1");
                  }}
                  aria-invalid={!!fieldErrors.address_line1}
                />
                {fieldErrors.address_line1 ? (
                  <p className="text-sm text-destructive">
                    {fieldErrors.address_line1}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1">
                <Label htmlFor="identity_address_line2">
                  Address line 2 (optional)
                </Label>
                <Input
                  id="identity_address_line2"
                  value={addressLine2}
                  autoComplete="address-line2"
                  maxLength={ADDRESS_LINE_MAX}
                  onChange={(e) => setAddressLine2(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="identity_postal_code">Postal code *</Label>
                  <Input
                    id="identity_postal_code"
                    value={postalCode}
                    autoComplete="postal-code"
                    maxLength={POSTAL_CODE_MAX}
                    onChange={(e) => {
                      setPostalCode(e.target.value);
                      clearFieldError("postal_code");
                    }}
                    aria-invalid={!!fieldErrors.postal_code}
                  />
                  {fieldErrors.postal_code ? (
                    <p className="text-sm text-destructive">
                      {fieldErrors.postal_code}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="identity_city">City *</Label>
                  <Input
                    id="identity_city"
                    value={city}
                    autoComplete="address-level2"
                    maxLength={CITY_MAX}
                    onChange={(e) => {
                      setCity(e.target.value);
                      clearFieldError("city");
                    }}
                    aria-invalid={!!fieldErrors.city}
                  />
                  {fieldErrors.city ? (
                    <p className="text-sm text-destructive">{fieldErrors.city}</p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="identity_country_code">Country *</Label>
                  <Input
                    id="identity_country_code"
                    value={countryCode}
                    autoComplete="country"
                    maxLength={2}
                    placeholder="DK"
                    onChange={(e) => {
                      setCountryCode(normalizeCountry(e.target.value));
                      clearFieldError("country_code");
                    }}
                    aria-invalid={!!fieldErrors.country_code}
                    aria-describedby="identity_country_code_hint"
                  />
                  <p id="identity_country_code_hint" className="text-xs opacity-60">
                    2-letter code, e.g. DK, GB, DE.
                  </p>
                  {fieldErrors.country_code ? (
                    <p className="text-sm text-destructive">
                      {fieldErrors.country_code}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm font-medium" id="identity_status_label">
                  You are: *
                </p>
                <div
                  role="radiogroup"
                  aria-labelledby="identity_status_label"
                  aria-invalid={!!fieldErrors.business_status}
                  className="flex flex-col gap-2 sm:flex-row sm:gap-6"
                >
                  <label
                    htmlFor="identity_status_private"
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      id="identity_status_private"
                      type="radio"
                      name="identity_business_status"
                      value="private"
                      checked={businessStatus === "private"}
                      onChange={() => {
                        setBusinessStatus("private");
                        // A private individual cannot hold one, and the server
                        // refuses the pair rather than dropping half of a
                        // contradictory declaration. Clear it here so the form
                        // never posts something it knows will be refused.
                        setRegistrationNumber("");
                        clearFieldError("business_status");
                        clearFieldError("business_registration_number");
                      }}
                    />
                    a private individual
                  </label>
                  <label
                    htmlFor="identity_status_business"
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      id="identity_status_business"
                      type="radio"
                      name="identity_business_status"
                      value="business"
                      checked={businessStatus === "business"}
                      onChange={() => {
                        setBusinessStatus("business");
                        clearFieldError("business_status");
                      }}
                    />
                    a business
                  </label>
                </div>
                {fieldErrors.business_status ? (
                  <p className="text-sm text-destructive">
                    {fieldErrors.business_status}
                  </p>
                ) : null}
              </div>

              {businessStatus === "business" ? (
                <div className="space-y-1">
                  <Label htmlFor="identity_registration_number">
                    Registration number (only if you have one)
                  </Label>
                  <Input
                    id="identity_registration_number"
                    value={registrationNumber}
                    maxLength={REGISTRATION_NUMBER_MAX}
                    onChange={(e) => {
                      setRegistrationNumber(e.target.value);
                      clearFieldError("business_registration_number");
                    }}
                    aria-invalid={!!fieldErrors.business_registration_number}
                  />
                  {fieldErrors.business_registration_number ? (
                    <p className="text-sm text-destructive">
                      {fieldErrors.business_registration_number}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                <Button onClick={submit} disabled={update.isPending}>
                  {update.isPending ? "Saving…" : "Save my details"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(false);
                    setFieldErrors({});
                  }}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
